import type { OnboardingSessionType, OnboardingUserType } from "@shared/types/OnboardingSession";
import { writable, get } from "svelte/store";
import { v4 as uuidv4 } from "uuid";

const storageKey = "onboardingSession";
const existingOnboardingSession = localStorage.getItem(storageKey);

const initValue: OnboardingSessionType = existingOnboardingSession
  ? JSON.parse(existingOnboardingSession)
  : null;

const createWritableStore = (initValue: OnboardingSessionType) => {
  const { subscribe, set, update } = writable(initValue);

  const destroyOnboardingSession = () => {
    if (!!localStorage.getItem(storageKey)) localStorage.removeItem(storageKey);

    set(null);
    window.removeEventListener("storage", localStorageListener);
  };

  // This listener will only fire when multiple tabs or windows are open. The manual clearing
  // is necessary for standard use-cases as changes in storage won't be detected in the same
  // window/tab scope.
  const localStorageListener = (_e: Event) => {
    const storedOnboardingSession = localStorage.getItem(storageKey);

    if (storedOnboardingSession === null) {
      set(null);
      return;
    }

    const localValue = JSON.parse(storedOnboardingSession);

    if (localValue.session_id !== get(OnboardingSessionStore).session_id) set(localValue);
  };

  const initSession = (type?: OnboardingUserType) => {
    const existingOnboardingSession = localStorage.getItem(storageKey);
    if (existingOnboardingSession) {
      set(JSON.parse(existingOnboardingSession));
    } else {
      let sessionData: OnboardingSessionType = { session_id: uuidv4(), onboarding_user_type: type };

      set(sessionData);
    }

    subscribe(onboardingSession => {
      if (onboardingSession && !onboardingSession.session_id) destroyOnboardingSession();
      else localStorage.setItem(storageKey, JSON.stringify(onboardingSession));
    });

    window.addEventListener("storage", localStorageListener);
  };

  return {
    subscribe,
    set,
    initSession,
    destroyOnboardingSession,
    update,
  };
};

export const OnboardingSessionStore = createWritableStore(initValue);
